import { db, filesystem, services } from "@/services/module_api";

const INSTALL_ID_KEY = "install_id";
let installGuardRan = false;
let installGuardWiped = false;

function generateInstallId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readInstallId(): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(INSTALL_ID_KEY);
  } catch {
    return null;
  }
}

function writeInstallId(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(INSTALL_ID_KEY, generateInstallId());
  } catch {
    // ignore storage errors
  }
}

async function performFreshInstallWipe(): Promise<void> {
  if (typeof localStorage !== "undefined") {
    const keys = [
      "apiToken",
      "communityToken",
      "displayPhotoUrl",
      "displayPhotoKey",
      "account_mnemonic",
      "auth_keypair",
      "balance",
      "llm-messages",
    ];
    for (const key of keys) {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore storage errors
      }
    }
  }

  try {
    const { auth } = await import("@/core_bridge/module_api");
    await auth.logout();
  } catch (err) {
    console.warn("[bootstrap/install] core logout failed", err);
  }

  try {
    await filesystem.clearAll();
  } catch (err) {
    console.warn("[bootstrap/install] filesystem clear failed", err);
  }

  try {
    services.router?.navigate?.("/welcome", { replace: true });
  } catch {
    // ignore navigation errors during early bootstrap
  }
}

export async function bootInstallGuard(): Promise<boolean> {
  if (installGuardRan) return installGuardWiped;
  installGuardRan = true;

  const installId = readInstallId();

  let appState:
    | { appToken?: string; publicKey?: string; secretKey?: string }
    | undefined;
  try {
    appState = await db.getAppState();
  } catch {
    appState = undefined;
  }

  const hasSession = !!appState?.appToken;
  const hasKeypair = !!appState?.publicKey && !!appState?.secretKey;
  const hasSecureData = hasSession || hasKeypair;
  const keypairMissingWithSession = hasSession && !hasKeypair;

  if ((installId == null && hasSecureData) || keypairMissingWithSession) {
    console.warn("[bootstrap/install] fresh install detected; wiping local data");
    await performFreshInstallWipe();
    installGuardWiped = true;
  }

  if (!readInstallId()) {
    writeInstallId();
  }

  return installGuardWiped;
}
